import { Injectable } from '@angular/core';
import { Magic } from 'magic-sdk';
import { OAuthExtension } from '@magic-ext/oauth2';
import { SOCIAL_LOGIN } from '../common/interface/common.interface';
import { environment } from '../../environments/environment';
import { ENABLE_TRADING_SIGNATURE } from '../common/constants/signature.constants';
import { ethers } from 'ethers';

@Injectable({
  providedIn: 'root'
})
export class Web2ServiceService {
  magic: any;  
  constructor() {}

  init() {
    this.magic = new Magic(environment.MAGIC_LINK_APIKEY, {
      network: {
        rpcUrl: "https://polygon-amoy.drpc.org/",
        chainId: 80002
      },
      extensions: [new OAuthExtension()],
      useStorageCache: true,
      deferPreload: true
    });
  }

  // sign-in/sign-up using email otp or sms otp
  async loginWithPayload(option: string = "email", payload: any): Promise<any> {
    if (!this.magic)
      this.init();

    if (option == "email_otp")
      return await this.magic.auth.loginWithEmailOTP({ "email": payload.email });
    else if (option == "email_link")
      return await this.magic.auth.loginWithMagicLink({ "email": payload.email });
    else
      return await this.magic.auth.loginWithSMS({ "phoneNumber": payload.mobile });
  }

  // sign-in/sign-up using social network
  async loginWithSocial(socialMedia: SOCIAL_LOGIN) {
    if (!this.magic)
      this.init();

    return await this.magic.oauth2.loginWithRedirect({
      "provider": socialMedia,
      "redirectURI": environment.MAGIC_REDIRECT_URI 
    })
  }

  // get user session after social network
  async getOAuthResult(): Promise<any> {
    if (!this.magic)
      this.init();

    return await this.magic.oauth2.getRedirectResult();
  }

  // verify is user is active
  async getAuthResult(): Promise<boolean> {
    if (!this.magic)
      this.init();

    return await this.magic.user.isLoggedIn();
  }

  // get did token for logged user
  async getAuthToken(): Promise<string> {
    if (!this.magic)
      this.init();

    return await this.magic.user.getIdToken();
  }

  async getUserMetaData(): Promise<any> {
    if (!this.magic)
      this.init();

    return await this.magic.user.getInfo();
  }

  async logout(): Promise<any> {
    let isLogged = await this.getAuthResult();
    if (isLogged) {
      await this.magic.user.logout();
      this.magic = null;
    }

    if (await this.getAuthResult())
      return this.logout();
    else
      return this.getAuthResult();
  }

  async generateSignature(address: string): Promise<string> {
    if (!this.magic)
      this.init();

    const payload = {
      ...ENABLE_TRADING_SIGNATURE,
      domain: {
        ...ENABLE_TRADING_SIGNATURE.domain,
        chainId: 80002
      }
    };

    let params: Array<string> = [address, JSON.stringify(payload)];
    return await this.magic.rpcProvider.request({
      method: 'eth_signTypedData_v4',
      params: params
    });
  }

  async signTypedData(address: string, payload: any): Promise<string> {
    if (!this.magic)
      this.init();

    let params: Array<string> = [address, JSON.stringify(payload)];
    return await this.magic.rpcProvider.request({
      method: 'eth_signTypedData_v4',
      params: params
    });
  }
  
  async getPrivateKey(): Promise<any> {
    return await this.magic.user.revealEVMPrivateKey();
  }

  async signRawHash(address: string, hash: string): Promise<string> {
    if (!this.magic)
      this.init();

    // personal_sign prepends the Ethereum prefix message, matching what signMessage({ raw }) does in Wagmi
    return await this.magic.rpcProvider.request({
      method: 'personal_sign',
      params: [hash, address]
    });
  }

  async signTransaction(tx: any): Promise<string> {
    if (!this.magic)
      this.init();

    const provider = new ethers.BrowserProvider(this.magic.rpcProvider);
    const signer = await provider.getSigner();
    return await signer.signTransaction(tx);
  }
}